<template>
  <Sidebar
    :visible="visible"
    position="left"
    class="flow-history-panel w-25rem"
    :show-close-icon="false"
    :dismissable="true"
    @update:visible="$emit('close')"
  >
    <div class="flow-history-panel__header">
      <h2>{{ t('flows.historyPanel.title') }}</h2>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <!-- Info note -->
    <div class="flow-history-panel__note">
      <i class="far fa-info-circle" />
      {{ t('flows.historyPanel.limitNote') }}
    </div>

    <div class="flow-history-panel__content">
      <!-- Undo stack (most recent first) -->
      <div v-if="undoEntries.length > 0" class="flow-history-panel__section">
        <div class="flow-history-panel__section-title">
          {{ t('flows.historyPanel.undoHistory') }}
        </div>
        <div
          v-for="(entry, index) in undoEntries"
          :key="entry.id"
          class="flow-history-panel__item"
          @click="restoreEntry(entry.originalIndex)"
        >
          <div class="flow-history-panel__item-icon">
            <i class="far fa-clock-rotate-left" />
          </div>
          <div class="flow-history-panel__item-info">
            <div class="flow-history-panel__item-label">
              {{ formatActionType(entry.actionType) }}
              <span v-if="formatMetaLabel(entry.meta, entry.actionType, entry.snapshot)" class="flow-history-panel__item-description">
                "{{ formatMetaLabel(entry.meta, entry.actionType, entry.snapshot) }}"
              </span>
            </div>
            <div class="flow-history-panel__item-time">
              {{ formatTime(entry.timestamp) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Redo stack -->
      <div v-if="redoEntries.length > 0" class="flow-history-panel__section">
        <div class="flow-history-panel__section-title">
          {{ t('flows.historyPanel.redoHistory') }}
        </div>
        <div
          v-for="entry in redoEntries"
          :key="entry.id"
          class="flow-history-panel__item flow-history-panel__item--redo"
        >
          <div class="flow-history-panel__item-icon">
            <i class="far fa-clock-rotate-left" />
          </div>
          <div class="flow-history-panel__item-info">
            <div class="flow-history-panel__item-label">
              {{ formatActionType(entry.actionType) }}
              <span v-if="formatMetaLabel(entry.meta, entry.actionType, entry.snapshot)" class="flow-history-panel__item-description">
                "{{ formatMetaLabel(entry.meta, entry.actionType, entry.snapshot) }}"
              </span>
            </div>
            <div class="flow-history-panel__item-time">
              {{ formatTime(entry.timestamp) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="undoEntries.length === 0 && redoEntries.length === 0"
        class="flow-history-panel__empty"
      >
        {{ t('flows.historyPanel.noHistory') }}
      </div>
    </div>
  </Sidebar>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import type { HistoryEntry, FlowActionType, HistoryEntryMeta } from '@/composables/flows/useFlowHistory';
import type { FlowStructureDto } from '@/types/flow-structure';
import { formatHistoryMetaLabel } from '@/composables/flows/useFlowEditor';

export default defineComponent({
  name: 'FlowHistoryPanel',
  components: {
    Sidebar,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    undoStack: {
      type: Array as PropType<HistoryEntry[]>,
      default: () => [],
    },
    redoStack: {
      type: Array as PropType<HistoryEntry[]>,
      default: () => [],
    },
  },
  emits: ['close', 'restore'],
  setup(props, { emit }) {
    const { t } = useI18n();

    // Reverse undo stack to show most recent first, and keep original index
    const undoEntries = computed(() => {
      return props.undoStack
        .map((entry, index) => ({ ...entry, originalIndex: index }))
        .reverse();
    });

    // Reverse redo stack to show most recent first
    const redoEntries = computed(() => {
      return [...props.redoStack].reverse();
    });

    const formatActionType = (actionType: FlowActionType): string => {
      return t(`flows.history.actions.${actionType}`);
    };

    // Format description based on actionType and meta params
    const formatMetaLabel = (meta: HistoryEntryMeta | undefined, actionType: FlowActionType, snapshot?: FlowStructureDto): string | null => {
      return formatHistoryMetaLabel(actionType, meta, snapshot);
    };

    const formatTime = (timestamp: number): string => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      const diffDays = Math.floor(diffMs / 86400000);
      const diffMonths = Math.floor(diffMs / 2592000000);
      const diffYears = Math.floor(diffMs / 31536000000);

      if (diffMins < 1) {
        return t('flows.historyPanel.justNow');
      } else if (diffMins < 60) {
        return t('flows.historyPanel.minutesAgo', { count: diffMins });
      } else if (diffHours < 24) {
        return t('flows.historyPanel.hoursAgo', { count: diffHours });
      } else if (diffDays < 30) {
        return t('flows.historyPanel.daysAgo', { count: diffDays });
      } else if (diffMonths < 12) {
        return t('flows.historyPanel.monthsAgo', { count: diffMonths });
      } else {
        return t('flows.historyPanel.yearsAgo', { count: diffYears });
      }
    };

    const restoreEntry = (originalIndex: number) => {
      emit('restore', originalIndex);
    };

    return {
      t,
      undoEntries,
      redoEntries,
      formatActionType,
      formatMetaLabel,
      formatTime,
      restoreEntry,
    };
  },
});
</script>

<style lang="scss">
.flow-history-panel {
  .p-sidebar-header {
    display: none !important;
  }

  .p-sidebar-content {
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    height: 100%;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }

    .p-button {
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      padding: 0 !important;
      color: #9ca3af !important;
      background: transparent !important;
      border: none !important;
      border-radius: 50% !important;

      &:hover {
        background: #f3f4f6 !important;
        color: #6b7280 !important;
      }

      &:focus {
        box-shadow: none !important;
      }
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem;
  }

  &__section {
    margin-bottom: 1rem;
  }

  &__section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    padding: 0.5rem 0.75rem;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background-color 0.15s;
    margin-bottom: 0.25rem;

    &:hover {
      background-color: #f3f4f6;
    }

    &--redo {
      opacity: 0.6;
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }
  }

  &__item-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  &__item-info {
    flex: 1;
    min-width: 0;
  }

  &__item-label {
    font-size: 0.8125rem;
    color: #374151;
    font-weight: 500;
    line-height: 1.4;
  }

  &__item-description {
    font-weight: 400;
    color: #6b7280;
  }

  &__item-time {
    font-size: 0.6875rem;
    color: #9ca3af;
    margin-top: 0.125rem;
  }

  &__empty {
    padding: 3rem 1.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.8125rem;
  }

  &__note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.4;
    flex-shrink: 0;

    i {
      color: #9ca3af;
      margin-top: 0.125rem;
      flex-shrink: 0;
    }
  }
}
</style>
